/**
 * Elenco delle notizie raccolte dal motore (F-109).
 *
 * **Perché serve.** Finora le notizie si vedevano solo dentro la scheda di un
 * titolo, e quelle che non si agganciano a nessuno strumento — le `rss:`, che
 * fanno da contesto di mercato — non comparivano da nessuna parte: scaricate,
 * conservate, usate nei prompt, invisibili. Un dato che entra nelle decisioni e
 * non è ispezionabile è esattamente ciò che rende difficile capire un giudizio.
 *
 * Due distinzioni che la pagina tiene esplicite, perché altrove si perdono:
 *
 *  1. **chi pubblica** (la testata) e **chi consegna** (il provider) sono due
 *     cose diverse: la stessa notizia del Sole 24 Ore può arrivare da MarketAux
 *     o da un feed RSS, e sapere quale delle due catene ha funzionato è metà
 *     della diagnosi quando le notizie mancano;
 *  2. una notizia **senza titoli agganciati** non è un errore: è contesto di
 *     mercato, e ha un filtro proprio invece di sembrare una riga incompleta.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from '@/lib/format';
import { Badge, Card, EmptyState, Field, Select, Spinner, TableWrap } from '@/ui';
import { useNews } from './api';
import { NewsReviewCard } from './NewsReviewCard';
import './news.css';

const EMPTY = '—';

/** Sentiment del provider, in −1..1. Colorato solo dove è netto: sotto quella
 *  soglia il segno è rumore, e dipingerlo suggerirebbe una precisione assente. */
function SentimentBadge({ value }: { value: number | null }) {
  if (value === null) return <span className="cell--minor">{EMPTY}</span>;
  const tone = value > 0.2 ? 'positive' : value < -0.2 ? 'negative' : 'neutral';
  return <Badge tone={tone}>{value.toFixed(2)}</Badge>;
}

export function NewsPage() {
  const [query, setQuery] = useState('');
  const [provider, setProvider] = useState('');
  const [contextOnly, setContextOnly] = useState(false);

  const news = useNews({
    q: query.trim() || undefined,
    provider: provider || undefined,
    contextOnly: contextOnly || undefined,
  });

  return (
    <div className="news-page">
      <header className="page-header">
        <div>
          <h1>Notizie</h1>
          <p className="page-header__meta">
            Quello che il motore ha raccolto e usa nei prompt, con la fonte e i titoli a cui è
            agganciato.
          </p>
        </div>
      </header>

      {/* F-185: la lettura agentica sta qui, sopra all'elenco che legge. */}
      <NewsReviewCard />

      <Card>
        <div className="news-page__filters">
          <Field
            label="Cerca"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Titolo o testata…"
            hint="Cerca nel titolo della notizia e nel nome della testata."
          />
          <Select
            label="Consegnato da"
            value={provider}
            onChange={(event) => setProvider(event.target.value)}
            placeholder="Tutti i provider"
            // Popolato con i provider REALMENTE presenti: un elenco scritto a
            // mano inviterebbe a filtrare per qualcosa che non c'è.
            options={(news.data?.providers ?? []).map((name) => ({ value: name, label: name }))}
          />
          <Select
            label="Tipo"
            value={contextOnly ? 'context' : ''}
            onChange={(event) => setContextOnly(event.target.value === 'context')}
            options={[
              { value: '', label: 'Tutte' },
              { value: 'context', label: 'Solo contesto di mercato' },
            ]}
            hint="«Contesto di mercato» sono le notizie non agganciate a nessun titolo."
          />
        </div>
      </Card>

      {news.isPending && <Spinner label="Caricamento delle notizie…" />}

      {news.data && news.data.items.length === 0 && (
        <EmptyState
          icon="📰"
          title="Nessuna notizia"
          description="Nessuna riga corrisponde ai filtri, oppure la raccolta non è ancora passata."
        />
      )}

      {news.data && news.data.items.length > 0 && (
        <Card title={`${news.data.total} notizie`} subtitle={`Mostrate le ${news.data.items.length} più recenti.`} flush>
          <TableWrap>
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Titolo</th>
                  <th>Testata</th>
                  <th>Consegnata da</th>
                  <th>Titoli</th>
                  <th className="numeric">Sentiment</th>
                </tr>
              </thead>
              <tbody>
                {news.data.items.map((item) => (
                  <tr key={item.id}>
                    <td className="cell--minor">
                      {/* Se manca la data di pubblicazione si mostra quando
                          l'abbiamo presa, dichiarandolo: una data inventata
                          sarebbe peggio di una dichiarata approssimativa. */}
                      {item.publishedAt
                        ? formatDateTime(item.publishedAt)
                        : `raccolta ${formatDateTime(item.fetchedAt)}`}
                    </td>
                    <td>
                      <a href={item.url} target="_blank" rel="noreferrer" className="news-page__title">
                        {item.title}
                      </a>
                    </td>
                    <td className="cell--minor">{item.source ?? EMPTY}</td>
                    <td className="cell--minor">{item.provider ?? EMPTY}</td>
                    <td>
                      {item.instruments.length === 0 ? (
                        <span className="cell--minor">contesto di mercato</span>
                      ) : (
                        <span className="news-page__instruments">
                          {item.instruments.map((link) => (
                            <Link key={link.instrumentId} to={`/instruments/${link.instrumentId}`}>
                              {link.symbol ?? link.name}
                            </Link>
                          ))}
                        </span>
                      )}
                    </td>
                    <td className="numeric">
                      <SentimentBadge value={item.sentiment} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Card>
      )}
    </div>
  );
}
